import React, { useEffect, useState } from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { RevenueData, vendorGetRevenue } from "../vendorApi";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend);

/* ═══════════════════════════════════════════════════════════════════════════
   VENDOR REVENUE PAGE — Full revenue analytics view
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  navy: "#0f172a",
  gold: "#d4a373",
  white: "#ffffff",
  slate: "#64748b",
  slateLt: "#94a3b8",
  line: "#e2e8f0",
};

const formatCurrency = (v: number) =>
  `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_CHIP: Record<string, { bg: string; color: string }> = {
  paid:      { bg: "#dcfce7", color: "#166534" },
  pending:   { bg: "#fef3c7", color: "#92400e" },
  cancelled: { bg: "#fef2f2", color: "#991b1b" },
};

const VendorRevenue: React.FC = () => {
  const [data, setData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    vendorGetRevenue()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const barConfig = data
    ? {
        data: {
          labels: data.chart.labels,
          datasets: [
            {
              label: "Revenue",
              data: data.chart.values,
              backgroundColor: "rgba(212,163,115,0.65)",
              hoverBackgroundColor: T.gold,
              borderRadius: 6,
              borderSkipped: false as const,
              barPercentage: 0.6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { intersect: false, mode: "index" as const },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: T.navy,
              titleFont: { size: 12, weight: 600 as const },
              bodyFont: { size: 13 },
              padding: 12,
              cornerRadius: 8,
              displayColors: false,
              callbacks: { label: (ctx: any) => formatCurrency(ctx.raw as number) },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { font: { size: 11 }, color: T.slateLt },
              border: { display: false },
            },
            y: {
              grid: { color: "rgba(0,0,0,0.04)" },
              ticks: {
                font: { size: 11 }, color: T.slateLt,
                callback: (v: any) => (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`),
              },
              border: { display: false },
              beginAtZero: true,
            },
          },
        },
      }
    : null;

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 16 }}>
        <CircularProgress size={32} sx={{ color: T.gold }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: T.navy, letterSpacing: "-0.02em" }}>
          Revenue
        </Typography>
        <Typography sx={{ fontSize: 14, color: T.slate, mt: 0.3 }}>
          Detailed revenue analytics and transaction history
        </Typography>
      </Box>

      {/* Summary row */}
      {data && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 2, mb: 3,
          }}
        >
          {[
            { label: "Total Revenue", value: formatCurrency(data.total_revenue) },
            { label: "This Month", value: formatCurrency(data.monthly_revenue) },
            { label: "Upcoming", value: formatCurrency(data.upcoming_revenue) },
            { label: "Avg per Booking", value: formatCurrency(data.avg_booking_value) },
          ].map((s) => (
            <Box
              key={s.label}
              sx={{
                bgcolor: T.white, borderRadius: "12px", border: `1px solid ${T.line}`,
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)", px: 2.5, py: 2,
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: T.navy, mt: 0.5 }}>
                {s.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Bar chart */}
      <Box
        sx={{
          bgcolor: T.white, borderRadius: "14px", border: `1px solid ${T.line}`,
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)", p: 3, mb: 3,
        }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.navy, mb: 2 }}>
          Monthly Revenue
        </Typography>
        <Box sx={{ height: 320 }}>
          {barConfig ? (
            <Bar data={barConfig.data} options={barConfig.options} />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <Typography sx={{ fontSize: 13, color: T.slateLt }}>No data available</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Full transaction list */}
      <Box
        sx={{
          bgcolor: T.white, borderRadius: "14px", border: `1px solid ${T.line}`,
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)", overflow: "hidden",
        }}
      >
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${T.line}` }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.navy }}>All Transactions</Typography>
        </Box>
        {!data || data.recent_transactions.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <ReceiptLongOutlinedIcon sx={{ fontSize: 40, color: T.slateLt, opacity: 0.4, mb: 1 }} />
            <Typography sx={{ fontSize: 13, color: T.slateLt }}>No transactions recorded yet</Typography>
          </Box>
        ) : (
          data.recent_transactions.map((txn, i) => (
            <Box
              key={txn.id}
              sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                px: 3, py: 2,
                borderBottom: i < data.recent_transactions.length - 1 ? `1px solid ${T.line}` : "none",
                transition: "background 0.12s",
                "&:hover": { bgcolor: "#fafbfd" },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>
                  {txn.event_name || txn.description || "Transaction"}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 0.3 }}>
                  {txn.booking_ref && (
                    <Typography sx={{ fontSize: 11, color: T.slateLt }}>{txn.booking_ref}</Typography>
                  )}
                  <Typography sx={{ fontSize: 11, color: T.slateLt }}>
                    {new Date(txn.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Chip
                  label={txn.status.charAt(0).toUpperCase() + txn.status.slice(1)}
                  size="small"
                  sx={{
                    height: 22, fontSize: 10, fontWeight: 600,
                    bgcolor: STATUS_CHIP[txn.status]?.bg || "#f1f5f9",
                    color: STATUS_CHIP[txn.status]?.color || T.slate,
                  }}
                />
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy, minWidth: 80, textAlign: "right" }}>
                  {formatCurrency(txn.amount)}
                </Typography>
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default VendorRevenue;
